import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Divider,
} from "@mui/material";
import { ArrowBack, Savings } from "@mui/icons-material";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import {
  CASH_OUT_MATRIX_MAX_LEVEL,
  getCashOutMatrixLevelAmount,
} from "../config/cashOutMatrixConfig";
import {
  getTotalCashOutAvailable,
  maxCashOutWithdrawalRequest,
} from "../config/cashOutWithdrawalConfig";
import { explainCashOutLedgerRow } from "../services/cashOut/cashOutLedgerExplain";

const formatDate = (v) => {
  if (!v) return "—";
  try {
    const d = v.toDate ? v.toDate() : new Date(v);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
};

const CashOutProgramDashboard = ({
  user,
  userProfile,
  onBack,
  backLabel = "Back",
}) => {
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [program, setProgram] = useState(null);
  const [positions, setPositions] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [userEmailByUid, setUserEmailByUid] = useState({});
  const [error, setError] = useState("");
  const [ledgerFilter, setLedgerFilter] = useState("all");

  const cashOutBox = userProfile?.cashOutBox || {
    earned: 0,
    available: 0,
    withdrawn: 0,
  };
  const cashOutCashWallet = userProfile?.cashOutCashWallet || {
    earned: 0,
    available: 0,
    withdrawn: 0,
  };
  const cashOutUpgrade = userProfile?.cashOutUpgrade || {
    reserved: 0,
    used: 0,
  };

  const load = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setError("");
    try {
      const progSnap = await getDocs(
        query(collection(db, "cashOutPrograms"), where("userId", "==", user.uid))
      );
      const progs = progSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort(
          (a, b) =>
            (Number(a.enrollmentSeq) || 0) - (Number(b.enrollmentSeq) || 0)
        );
      setPrograms(progs);
      setProgram((prev) => {
        if (prev && progs.some((p) => p.programId === prev.programId)) {
          return progs.find((p) => p.programId === prev.programId);
        }
        return progs[0] || null;
      });

      const ledgerSnap = await getDocs(
        query(collection(db, "cashOutLedger"), where("userId", "==", user.uid))
      );
      const rows = ledgerSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const at = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bt = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bt - at;
      });
      setLedger(rows);

      const fillerIds = [
        ...new Set(rows.map((r) => r.fillerUserId).filter(Boolean)),
      ];
      const emailMap = {};
      for (const uid of fillerIds) {
        const uq = await getDocs(
          query(collection(db, "users"), where("uid", "==", uid))
        );
        if (!uq.empty) {
          emailMap[uid] = uq.docs[0].data().email || uid;
        }
      }
      setUserEmailByUid(emailMap);
    } catch (e) {
      console.error(e);
      setError(e.message || "Failed to load Cash Out program");
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  const loadPositions = useCallback(async (prog) => {
    if (!prog?.id) {
      setPositions([]);
      return;
    }
    const snap = await getDocs(
      query(
        collection(db, "cashOutMatrixPositions"),
        where("programDocId", "==", prog.id)
      )
    );
    const rows = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort(
        (a, b) =>
          (a.level || 0) - (b.level || 0) ||
          (a.positionIndex || 0) - (b.positionIndex || 0)
      );
    setPositions(rows);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (program) loadPositions(program);
  }, [program, loadPositions]);

  const filteredLedger = useMemo(() => {
    if (ledgerFilter === "program" && program?.programId) {
      return ledger.filter((r) => r.programId === program.programId);
    }
    return ledger;
  }, [ledger, ledgerFilter, program?.programId]);

  const level = program?.currentLevel || 1;
  const levelUnit = getCashOutMatrixLevelAmount(level);
  const totalAvailable = getTotalCashOutAvailable(cashOutBox, cashOutCashWallet);
  const maxMove = maxCashOutWithdrawalRequest(cashOutBox, cashOutCashWallet);

  const enrollmentFill = program?.matrixFill;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: primaryBlue[50], py: 3 }}>
      <Box sx={{ maxWidth: 960, mx: "auto", px: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
          {backLabel}
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          <Savings sx={{ mr: 1, verticalAlign: "middle" }} />
          My Cash Out Program
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          <CircularProgress />
        ) : !program ? (
          <Alert severity="info">
            No Cash Out Program yet. Enroll on cart (₹300 per ₹3,000 subtotal) to
            receive a COP ID.
          </Alert>
        ) : (
          <>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography color="text.secondary">Program ID</Typography>
                  {programs.length > 1 ? (
                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                      <Select
                        value={program.programId}
                        onChange={(e) => {
                          const next = programs.find(
                            (p) => p.programId === e.target.value
                          );
                          if (next) setProgram(next);
                        }}
                      >
                        {programs.map((p) => (
                          <MenuItem key={p.programId} value={p.programId}>
                            {p.programId} · seq #{p.enrollmentSeq}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="h6">{program.programId}</Typography>
                  )}
                  <Typography variant="caption" display="block" color="text.secondary">
                    Global enrollment #{program.enrollmentSeq}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography color="text.secondary">Enrolled from order</Typography>
                  <Typography variant="body1">
                    {program.sourceCartOrderId || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Fee ₹{program.enrollmentFeePerBlock || 300} · Subtotal ₹
                    {program.orderSubtotalAtEnrollment || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography color="text.secondary">Status</Typography>
                  <Chip
                    label={(program.status || "active").toUpperCase()}
                    color={program.status === "completed" ? "default" : "success"}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography color="text.secondary">Matrix level</Typography>
                  <Typography variant="h6">
                    {level} / {CASH_OUT_MATRIX_MAX_LEVEL}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Payment unit at this level: ₹{levelUnit}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(level / CASH_OUT_MATRIX_MAX_LEVEL) * 100}
                    sx={{ mt: 1 }}
                  />
                </Grid>
              </Grid>

              {enrollmentFill && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Your enrollment placed a matrix fill
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {enrollmentFill.applied
                      ? `Fill #${enrollmentFill.fillSeq} credited upline COP #${enrollmentFill.uplineIndex} (${enrollmentFill.uplineProgramId || "—"}) at their L${enrollmentFill.level || "?"}.`
                      : `Matrix fill not applied${enrollmentFill.reason ? `: ${enrollmentFill.reason}` : ""}.`}
                  </Typography>
                </>
              )}
            </Paper>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2">Cash Out earnings (total)</Typography>
                  <Typography variant="h5">₹{totalAvailable.toFixed(2)}</Typography>
                  <Typography variant="caption" display="block">
                    Box: ₹{(cashOutBox.available || 0).toFixed(2)} · Cash leg: ₹
                    {(cashOutCashWallet.available || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Move up to ₹{maxMove} to Cash Wallet.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2">Upgrade pool</Typography>
                  <Typography variant="h5">
                    ₹{(cashOutUpgrade.reserved || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="caption">
                    From matrix fills 2–3 toward next level
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2">Cycle progress</Typography>
                  <Typography variant="h5">
                    {program.levelPaymentsInCycle || 0} / 3 fills
                  </Typography>
                  <Typography variant="caption">
                    Payments received this level before upgrade / Step 4 split
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Matrix slots ({program.programId})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Three positions per level. When filled, you earn per the 4-level plan
                (box, upgrade pool, or Step 4 split).
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Level</TableCell>
                      <TableCell>Slot</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Filled by</TableCell>
                      <TableCell>When</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {positions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>No matrix positions yet.</TableCell>
                      </TableRow>
                    ) : (
                      positions.map((pos) => (
                        <TableRow key={pos.id}>
                          <TableCell>L{pos.level}</TableCell>
                          <TableCell>{pos.positionIndex}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={pos.positionStatus || "open"}
                              color={
                                pos.positionStatus === "filled" ? "success" : "default"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {pos.filledByProgramId ||
                              (pos.filledByUserId
                                ? userEmailByUid[pos.filledByUserId] ||
                                  pos.filledByUserId
                                : "—")}
                          </TableCell>
                          <TableCell>{formatDate(pos.filledAt)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="h6">Money flow &amp; ledger</Typography>
                <FormControl size="small">
                  <Select
                    value={ledgerFilter}
                    onChange={(e) => setLedgerFilter(e.target.value)}
                  >
                    <MenuItem value="all">All my COP activity</MenuItem>
                    <MenuItem value="program">This program only</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <List dense>
                {filteredLedger.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No transactions yet" />
                  </ListItem>
                ) : (
                  filteredLedger.slice(0, 40).map((row) => {
                    const ex = explainCashOutLedgerRow(row, {
                      userEmailByUid,
                      programId: program.programId,
                    });
                    return (
                      <ListItem key={row.id} alignItems="flex-start">
                        <ListItemText
                          primary={ex.title}
                          secondary={
                            <>
                              {ex.detail}
                              <br />
                              <Typography component="span" variant="caption">
                                {formatDate(row.createdAt)}
                                {row.programId ? ` · ${row.programId}` : ""}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                    );
                  })
                )}
              </List>
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CashOutProgramDashboard;
